const API_BASE = '';

/**
 * Check whether the current browser session is an external user session.
 * The __Host-FX_External cookie is set (non-HttpOnly) alongside the __Host-FX_Session
 * HttpOnly cookie during the OAuth callback, and cleared on logout.
 */
function isExternalSession(): boolean {
  return document.cookie.split(';').some(c => c.trim().startsWith('__Host-FX_External='));
}

/**
 * Resolve the correct API path prefix based on session type.
 * External users (authenticated via __Host-FX_Session cookie from OAuth) use
 * /public/external/* routes. Internal users (CF Access) use /api/*.
 *
 * Detection: __Host-FX_External cookie is set by the OAuth callback and cleared
 * on logout. In dev mode, always use /api/*.
 */
export function resolveApiPath(path: string): string {
  // Dev mode always uses /api/* with CF-Access-JWT-Assertion bypass
  if (import.meta.env.DEV) return path;

  // If no external session marker cookie, use the path as-is
  if (!isExternalSession()) return path;

  // Special case: /api/auth/me → /public/external/me
  if (path === '/api/auth/me') return '/public/external/me';

  // General case: /api/* → /public/external/*
  if (path.startsWith('/api/')) {
    return '/public/external/' + path.slice(5);
  }

  return path;
}

interface ApiError {
  code: string;
  message: string;
  details?: unknown;
}

interface ApiResponse<T> {
  success: boolean;
  data?: T;
  error?: ApiError;
}

class ApiClient {
  private baseUrl: string;

  constructor(baseUrl = API_BASE) {
    this.baseUrl = baseUrl;
  }

  private async request<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<T> {
    const url = `${this.baseUrl}${endpoint}`;
    
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
      'X-FX-CSRF': '1',
      ...options.headers,
    };

    // Only add dev auth headers in actual development mode (not production)
    if (import.meta.env.DEV) {
      const devEmail = localStorage.getItem('dev_user_email') || 'dev@example.com';
      (headers as Record<string, string>)['CF-Access-JWT-Assertion'] = 'dev';
      (headers as Record<string, string>)['CF-Access-Authenticated-User-Email'] = devEmail;
    }

    const response = await fetch(url, {
      ...options,
      headers,
      credentials: 'include',
      // Cloudflare Access answers an unauthenticated /api/* call with a redirect
      // to its own hostname. Following it turns a same-origin call into a
      // cross-origin one that cannot be preflighted mid-redirect, so it fails
      // CORS and logs an error for every anonymous visitor. Not following says
      // the same thing quietly. Redirect-following downloads use api.download().
      redirect: 'manual',
    });

    if (response.type === 'opaqueredirect') {
      throw new Error('Authentication required');
    }

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({
        error: { code: 'UNKNOWN', message: 'An unexpected error occurred' },
      }));
      throw new Error(errorData.error?.message || `HTTP ${response.status}`);
    }

    return response.json();
  }

  async get<T>(endpoint: string): Promise<T> {
    return this.request<T>(endpoint, { method: 'GET' });
  }

  async post<T>(endpoint: string, data?: unknown): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'POST',
      body: data ? JSON.stringify(data) : undefined,
    });
  }

  async patch<T>(endpoint: string, data?: unknown): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'PATCH',
      body: data ? JSON.stringify(data) : undefined,
    });
  }

  async put<T>(endpoint: string, data?: unknown): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'PUT',
      body: data ? JSON.stringify(data) : undefined,
    });
  }

  async delete<T>(endpoint: string): Promise<T> {
    return this.request<T>(endpoint, { method: 'DELETE' });
  }

  async download(endpoint: string): Promise<Blob> {
    const headers: HeadersInit = {};

    if (import.meta.env.DEV) {
      const devEmail = localStorage.getItem('dev_user_email') || 'dev@example.com';
      (headers as Record<string, string>)['CF-Access-JWT-Assertion'] = 'dev';
      (headers as Record<string, string>)['CF-Access-Authenticated-User-Email'] = devEmail;
    }

    const response = await fetch(`${this.baseUrl}${endpoint}`, {
      method: 'GET',
      headers,
      credentials: 'include',
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(errorText || `HTTP ${response.status}`);
    }

    return response.blob();
  }

  // Special method for file uploads (doesn't set Content-Type)
  async uploadFile(
    endpoint: string,
    formData: FormData,
    onProgress?: (progress: number) => void
  ): Promise<ApiResponse<unknown>> {
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open('POST', `${this.baseUrl}${endpoint}`);
      xhr.setRequestHeader('X-FX-CSRF', '1');

      // Add dev headers for local development
      if (import.meta.env.DEV) {
        const devEmail = localStorage.getItem('dev_user_email') || 'dev@example.com';
        xhr.setRequestHeader('CF-Access-JWT-Assertion', 'dev');
        xhr.setRequestHeader('CF-Access-Authenticated-User-Email', devEmail);
      }

      xhr.upload.onprogress = (event) => {
        if (event.lengthComputable && onProgress) {
          const progress = (event.loaded / event.total) * 100;
          onProgress(progress);
        }
      };

      xhr.onload = () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          resolve(JSON.parse(xhr.responseText));
        } else {
          reject(new Error(xhr.statusText || `HTTP ${xhr.status}`));
        }
      };

      xhr.onerror = () => reject(new Error('Network error'));
      xhr.send(formData);
    });
  }
}

export const api = new ApiClient();

// API Types
export interface User {
  id: string;
  email: string;
  displayName?: string;
  roles: string[];
  isActive: boolean;
  isExternal: boolean;
  /** Server-derived permission to declare CUI on this session; never inferred client-side. */
  canSendCui?: boolean;
  createdAt: string;
  lastLoginAt?: string;
}

export interface FileUpload {
  id: string;
  ownerId: string;
  uploadedByEmail?: string;
  fileName: string;
  originalFileName: string;
  mimeType: string;
  sizeBytes: number;
  r2Key?: string;
  // Collection info for grouping (supports both inbound and outbound)
  collectionId?: string | null;
  collectionDirection?: 'inbound' | 'outbound' | null;
  collectionRecipientsDisplay?: string | null; // For outbound: "email@example.com" or "3 recipients"
  collectionStaffName?: string | null; // For inbound: who set up the collection
  collectionStaffEmail?: string | null;
  collectionExpiresAt?: string | null; // For inbound: when the collection expires
  isEncrypted?: boolean;
  encryptionAlg?: string | null;
  encryptionSalt?: string | null;
  encryptionIv?: string | null;
  encryptionChunkSize?: number | null;
  isCui: boolean;
  cuiCategory?: string;
  retentionDays: number;
  expiresAt?: string;
  isDeleted: boolean;
  createdAt: string;
  updatedAt: string;
  // Download statistics
  downloadCount?: number;
}

export interface Share {
  id: string;
  fileId: string;
  createdBy: string;
  recipientEmail: string;
  accessToken: string;
  maxDownloads: number;
  downloadCount: number;
  expiresAt?: string;
  requiredAssurance: number;
  isRevoked: boolean;
  createdAt: string;
  file?: FileUpload;
}

export interface DownloadRecord {
  id: string;
  shareId: string;
  fileId: string;
  downloadedByEmail: string;
  ipAddress: string;
  downloadedAt: string;
}

export interface BlocklistEntry {
  id: string;
  type: 'domain' | 'email' | 'ip' | 'ip-range';
  value: string;
  reason?: string;
  isActive: boolean;
  createdAt: string;
}

export interface AuditLog {
  id: string;
  timestamp: string;
  action: string;
  actorId: string;
  actorEmail: string;
  resourceType: string;
  resourceId: string;
  details: Record<string, unknown>;
  ipAddress: string;
  cuiDesignation: boolean;
}

export interface RetentionPolicy {
  id: string;
  name: string;
  description?: string;
  retentionDays: number;
  appliesTo: 'all' | 'cui' | 'non-cui';
  isDefault: boolean;
  isActive: boolean;
}

export interface SystemSetting {
  key: string;
  value: string;
  description?: string;
}
